import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Divider,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Person,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Badge as BadgeIcon,
  CreditCard,
  CheckCircle,
  Download,
  EmojiEvents,
  Verified,
  Pending,
  Cancel,
  MoreVert,
  Logout,
  Receipt,
  Home,
  Close as CloseIcon,
  FamilyRestroom,
  MedicalServices,
  TrendingUp,
  Info,
  History,
  ExpandMore,
  Bloodtype,
  School,
  AccountBalance,
  Description,
  Warning,
} from '@mui/icons-material';
import { athleteAPI } from '../api/athlete.api';
import { paymentAPI } from '../api/payment.api';
import { useAuth } from '../context/AuthContext';

const PageWrapper = styled(Box)({
  minHeight: '100vh',
  backgroundColor: '#f8f9fa',
  padding: '2rem 0',
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const StatusCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(41, 30, 108, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const StyledButton = styled(Button)({
  padding: '10px 24px',
  borderRadius: '8px',
  fontSize: '0.95rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const ImagePreview = styled('img')({
  width: '100%',
  maxWidth: '200px',
  height: 'auto',
  borderRadius: '8px',
  border: '2px solid #e0e0e0',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
});

const InfoRow = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" mb={1.5}>
    <Box display="flex" alignItems="center" minWidth="180px">
      {icon}
      <Typography variant="body2" color="text.secondary" ml={1}>
        {label}:
      </Typography>
    </Box>
    <Typography variant="body1" fontWeight="500">
      {value || 'N/A'}
    </Typography>
  </Box>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await athleteAPI.getProfile();
      if (response.success) {
        setProfile(response.data);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceNumber, invoicePath) => {
    setDownloading(true);
    try {
      if (invoicePath) {
        const link = document.createElement('a');
        link.href = invoicePath;
        link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
        link.setAttribute('target', '_blank');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Invoice downloaded successfully');
      } else {
        toast.error('Invoice not available');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAdmitCard = async () => {
    setDownloading(true);
    try {
      const response = await athleteAPI.downloadAdmitCard();
      if (response.data.url) {
        const { url, filename } = response.data;
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        link.setAttribute('target', '_blank');
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Admit card downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading admit card:', error);
      toast.error('Failed to download admit card');
    } finally {
      setDownloading(false);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  const handleImagePreview = (imageUrl) => {
    setPreviewImage(imageUrl);
    setOpenImageDialog(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    if (typeof amount === 'number') {
      return (amount / 100).toFixed(2);
    }
    return amount || '0.00';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return <CheckCircle />;
      case 'pending':
        return <Pending />;
      case 'failed':
      case 'rejected':
        return <Cancel />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress size={60} sx={{ color: '#291e6c' }} />
          </Box>
        </Container>
      </PageWrapper>
    );
  }

  if (!profile) {
    return (
      <PageWrapper>
        <Container maxWidth="lg">
          <Alert severity="error">Failed to load profile data</Alert>
        </Container>
      </PageWrapper>
    );
  }

  const isPaymentCompleted = profile.paymentStatus === 'completed';
  const isVerified = profile.verificationStatus === 'approved';
  const hasAdmitCard = profile.admitCardGenerated;
  const canUpgrade = isPaymentCompleted && profile.selectedEvent?.eventName?.toLowerCase().includes('state');

  return (
    <PageWrapper>
      <Container maxWidth="lg">
        {/* Header with Menu */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography
            variant="h3"
            sx={{
              fontFamily: "'Oswald', sans-serif",
              fontWeight: 700,
              color: '#291e6c',
            }}
          >
            Athlete Dashboard
          </Typography>
          <IconButton onClick={handleMenuOpen} sx={{ color: '#291e6c' }}>
            <MoreVert />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/'); }}>
              <Home sx={{ mr: 1 }} /> Home
            </MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); handleLogout(); }}>
              <Logout sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Box>

        {/* Status Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatusCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <CreditCard sx={{ fontSize: 35, color: '#291e6c' }} />
                  <Chip
                    label={profile.paymentStatus}
                    color={getStatusColor(profile.paymentStatus)}
                    size="small"
                    icon={getStatusIcon(profile.paymentStatus)}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Payment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isPaymentCompleted ? `Paid: ₹${formatAmount(profile.amount)}` : 'Not Completed'}
                </Typography>
              </CardContent>
            </StatusCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatusCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Verified sx={{ fontSize: 35, color: '#291e6c' }} />
                  <Chip
                    label={profile.verificationStatus}
                    color={getStatusColor(profile.verificationStatus)}
                    size="small"
                    icon={getStatusIcon(profile.verificationStatus)}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Verification
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isVerified ? 'Approved' : 'Pending Approval'}
                </Typography>
              </CardContent>
            </StatusCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatusCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <BadgeIcon sx={{ fontSize: 35, color: '#291e6c' }} />
                  <Chip
                    label={hasAdmitCard ? 'Available' : 'Pending'}
                    color={hasAdmitCard ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Admit Card
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {hasAdmitCard ? `Card #${profile.admitCardNumber}` : 'Not Generated'}
                </Typography>
              </CardContent>
            </StatusCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatusCard>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <EmojiEvents sx={{ fontSize: 35, color: '#291e6c' }} />
                  <Chip label="Active" color="success" size="small" />
                </Box>
                <Typography variant="h6" fontWeight="bold">
                  Event
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {profile.selectedEvent?.eventName || 'Not Selected'}
                </Typography>
              </CardContent>
            </StatusCard>
          </Grid>
        </Grid>

        {/* Upgrade Section */}
        {canUpgrade && (
          <Alert severity="info" icon={<TrendingUp />} sx={{ mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
              <Box>
                <Typography variant="body1" fontWeight="bold" mb={0.5}>
                  Upgrade to National (NIC) Level
                </Typography>
                <Typography variant="body2">
                  You can upgrade to National (NIC) by paying only the differential amount of ₹500 (plus taxes).
                </Typography>
              </Box>
              <StyledButton
                variant="contained"
                size="small"
                startIcon={<TrendingUp />}
                onClick={() => navigate('/upgrade')}
                sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' },marginLeft:2, mt: { xs: 2, md: 0 } }}
              >
                Upgrade Now
              </StyledButton>
            </Box>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <SectionHeader>
                <Person sx={{ fontSize: 28, color: '#291e6c' }} />
                <Typography variant="h5" fontWeight="bold">
                  Personal Information
                </Typography>
              </SectionHeader>

              <InfoRow
                icon={<Person sx={{ fontSize: 20, color: '#666' }} />}
                label="Full Name"
                value={`${profile.firstName} ${profile.middleName || ''} ${profile.surname}`.trim()}
              />
              <InfoRow
                icon={<FamilyRestroom sx={{ fontSize: 20, color: '#666' }} />}
                label="Father's Name"
                value={`${profile.fatherFirstName} ${profile.fatherMiddleName || ''} ${profile.fatherSurname}`.trim()}
              />
              {profile.motherFirstName && (
                <InfoRow
                  icon={<FamilyRestroom sx={{ fontSize: 20, color: '#666' }} />}
                  label="Mother's Name"
                  value={`${profile.motherFirstName} ${profile.motherMiddleName || ''} ${profile.motherSurname}`.trim()}
                />
              )}
              <InfoRow
                icon={<Email sx={{ fontSize: 20, color: '#666' }} />}
                label="Email"
                value={profile.email}
              />
              <InfoRow
                icon={<Phone sx={{ fontSize: 20, color: '#666' }} />}
                label="Phone"
                value={profile.phoneNumber}
              />
              <InfoRow
                icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                label="Date of Birth"
                value={formatDate(profile.birthDate)}
              />
              <InfoRow
                icon={<BadgeIcon sx={{ fontSize: 20, color: '#666' }} />}
                label="Gender"
                value={profile.gender}
              />
              <InfoRow
                icon={<BadgeIcon sx={{ fontSize: 20, color: '#666' }} />}
                label="Registration Type"
                value={profile.registrationType?.toUpperCase()}
              />
              {profile.medicalAge && (
                <InfoRow
                  icon={<MedicalServices sx={{ fontSize: 20, color: '#666' }} />}
                  label="Medical Age"
                  value={`${profile.medicalAge} years`}
                />
              )}
              {profile.bloodGroup && (
                <InfoRow
                  icon={<Bloodtype sx={{ fontSize: 20, color: '#666' }} />}
                  label="Blood Group"
                  value={profile.bloodGroup}
                />
              )}
              {profile.kheloIndiaId && (
                <InfoRow
                  icon={<School sx={{ fontSize: 20, color: '#666' }} />}
                  label="Khelo India ID"
                  value={profile.kheloIndiaId}
                />
              )}
              <InfoRow
                icon={<BadgeIcon sx={{ fontSize: 20, color: '#666' }} />}
                label="Aadhar Number"
                value={profile.aadharCardNumber}
              />
              {profile.district && (
                <InfoRow
                  icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                  label="District"
                  value={profile.district}
                />
              )}
              {profile.stateDepartment && (
                <InfoRow
                  icon={<AccountBalance sx={{ fontSize: 20, color: '#666' }} />}
                  label="State Department"
                  value={profile.stateDepartment}
                />
              )}
            </StyledPaper>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <SectionHeader>
                <Home sx={{ fontSize: 28, color: '#291e6c' }} />
                <Typography variant="h5" fontWeight="bold">
                  Address Information
                </Typography>
              </SectionHeader>

              <InfoRow
                icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                label="Address Line 1"
                value={profile.addressLine1}
              />
              {profile.addressLine2 && (
                <InfoRow
                  icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                  label="Address Line 2"
                  value={profile.addressLine2}
                />
              )}
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                label="City"
                value={profile.city}
              />
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                label="State"
                value={profile.state}
              />
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                label="Pincode"
                value={profile.pincode}
              />
              <InfoRow
                icon={<LocationOn sx={{ fontSize: 20, color: '#666' }} />}
                label="Country"
                value={profile.country}
              />
            </StyledPaper>

            {/* Photo Preview */}
            {profile.photo?.path && (
              <StyledPaper sx={{ mt: 3 }}>
                <SectionHeader>
                  <Person sx={{ fontSize: 28, color: '#291e6c' }} />
                  <Typography variant="h5" fontWeight="bold">
                    Profile Photo
                  </Typography>
                </SectionHeader>
                <Box display="flex" justifyContent="center">
                  <ImagePreview
                    src={profile.photo.path}
                    alt="Profile"
                    onClick={() => handleImagePreview(profile.photo.path)}
                  />
                </Box>
              </StyledPaper>
            )}
          </Grid>

          {/* Payment & Event Information */}
          <Grid item xs={12}>
            <StyledPaper>
              <SectionHeader>
                <EmojiEvents sx={{ fontSize: 28, color: '#291e6c' }} />
                <Typography variant="h5" fontWeight="bold">
                  Current Event Registration
                </Typography>
              </SectionHeader>

              {profile.selectedEvent ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ border: '2px solid #291e6c', backgroundColor: 'rgba(41, 30, 108, 0.05)' }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                          {profile.selectedEvent.eventName}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <InfoRow
                          icon={<CreditCard sx={{ fontSize: 20, color: '#666' }} />}
                          label="Event Amount"
                          value={`₹${profile.selectedEvent.eventAmount}`}
                        />
                        <InfoRow
                          icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                          label="Period"
                          value={profile.selectedEvent.eventPeriod}
                        />
                        <InfoRow
                          icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                          label="Valid From"
                          value={formatDate(profile.selectedEvent.eventStartDate)}
                        />
                        <InfoRow
                          icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                          label="Valid Until"
                          value={formatDate(profile.selectedEvent.eventEndDate)}
                        />
                        <InfoRow
                          icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                          label="Registered On"
                          value={formatDate(profile.selectedEvent.registeredAt)}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card sx={{ border: '2px solid #4caf50', backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom>
                          Latest Payment Details
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <InfoRow
                          icon={<Receipt sx={{ fontSize: 20, color: '#666' }} />}
                          label="Invoice Number"
                          value={profile.invoiceNumber}
                        />
                        <InfoRow
                          icon={<CreditCard sx={{ fontSize: 20, color: '#666' }} />}
                          label="Payment ID"
                          value={profile.paymentId}
                        />
                        <InfoRow
                          icon={<CheckCircle sx={{ fontSize: 20, color: '#666' }} />}
                          label="Payment Status"
                          value={
                            <Chip
                              label={profile.paymentStatus}
                              color={getStatusColor(profile.paymentStatus)}
                              size="small"
                              icon={getStatusIcon(profile.paymentStatus)}
                            />
                          }
                        />
                        <InfoRow
                          icon={<CreditCard sx={{ fontSize: 20, color: '#666' }} />}
                          label="Amount Paid"
                          value={`₹${formatAmount(profile.amount)}`}
                        />
                        <InfoRow
                          icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                          label="Payment Date"
                          value={formatDate(profile.paymentDate)}
                        />
                        
                        {isPaymentCompleted && (
                          <Box mt={2}>
                            <StyledButton
                              variant="contained"
                              fullWidth
                              startIcon={<Download />}
                              onClick={() => handleDownloadInvoice(profile.invoiceNumber, profile.invoicePath)}
                              disabled={downloading}
                              sx={{ backgroundColor: '#291e6c', '&:hover': { backgroundColor: '#1f1750' } }}
                            >
                              Download Latest Invoice
                            </StyledButton>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="warning">No event selected yet</Alert>
              )}
            </StyledPaper>
          </Grid>

          {/* Invoice History */}
          {profile.invoices && profile.invoices.length > 0 && (
            <Grid item xs={12}>
              <StyledPaper>
                <SectionHeader>
                  <History sx={{ fontSize: 28, color: '#291e6c' }} />
                  <Typography variant="h5" fontWeight="bold">
                    Invoice History
                  </Typography>
                </SectionHeader>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(41, 30, 108, 0.05)' }}>
                        <TableCell><strong>Invoice #</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Event</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell align="center"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {profile.invoices.map((invoice, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {invoice.invoiceNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={invoice.invoiceType === 'initial' ? 'Initial' : 'Upgrade'}
                              color={invoice.invoiceType === 'initial' ? 'primary' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {invoice.relatedEvent?.eventName || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              ₹{formatAmount(invoice.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(invoice.paymentDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Download Invoice">
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadInvoice(invoice.invoiceNumber, invoice.invoicePath)}
                                disabled={downloading}
                                sx={{ color: '#291e6c' }}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </StyledPaper>
            </Grid>
          )}

          {/* Upgrade History */}
          {profile.upgradeHistory && profile.upgradeHistory.length > 0 && (
            <Grid item xs={12}>
              <StyledPaper>
                <SectionHeader>
                  <TrendingUp sx={{ fontSize: 28, color: '#291e6c' }} />
                  <Typography variant="h5" fontWeight="bold">
                    Upgrade History
                  </Typography>
                </SectionHeader>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                        <TableCell><strong>From Event</strong></TableCell>
                        <TableCell><strong>To Event</strong></TableCell>
                        <TableCell><strong>Differential Amount</strong></TableCell>
                        <TableCell><strong>Upgrade Date</strong></TableCell>
                        <TableCell><strong>Invoice</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {profile.upgradeHistory.map((upgrade, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {upgrade.fromEvent?.eventName || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500" color="success.main">
                              {upgrade.toEvent?.eventName || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              ₹{upgrade.differentialAmount}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(upgrade.upgradeDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {upgrade.upgradeInvoiceNumber}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </StyledPaper>
            </Grid>
          )}



{/* Upgrade Section - Only show if eligible */}
{profile.paymentStatus === 'completed' && 
 profile.selectedEvent?.eventName?.toLowerCase().includes('state') && (
  <Grid item xs={12}>
    <StyledPaper>
      <SectionHeader>
        <TrendingUp sx={{ fontSize: 28, color: '#291e6c' }} />
        <Typography variant="h5" fontWeight="bold">
          Upgrade Your Registration
        </Typography>
      </SectionHeader>

      <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
        <Typography variant="body1" fontWeight="bold" mb={1}>
          Upgrade to National (NIC) Level
        </Typography>
        <Typography variant="body2">
          You are currently registered for State (SIC). Upgrade to National (NIC) by paying
          only the differential amount of ₹500 (plus taxes).
        </Typography>
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card sx={{ border: '2px solid #ff9800' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Current Registration
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {profile.selectedEvent?.eventName}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Amount Paid: ₹{profile.selectedEvent?.eventAmount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card sx={{ border: '2px solid #4caf50', backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  Upgrade To
                </Typography>
                <Chip label="Recommended" color="success" size="small" />
              </Box>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                National (NIC)
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={1}>
                Pay only: ₹500 (differential)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box mt={3} display="flex" justifyContent="center">
        <StyledButton
          variant="contained"
          size="large"
          startIcon={<TrendingUp />}
          onClick={() => navigate('/upgrade')}
          sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
        >
          Upgrade to National (NIC)
        </StyledButton>
      </Box>
    </StyledPaper>
  </Grid>
)}

          {/* Admit Card Section */}
          <Grid item xs={12}>
            <StyledPaper>
              <SectionHeader>
                <BadgeIcon sx={{ fontSize: 28, color: '#291e6c' }} />
                <Typography variant="h5" fontWeight="bold">
                  Admit Card
                </Typography>
              </SectionHeader>

              {hasAdmitCard ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ border: '2px solid #4caf50', backgroundColor: 'rgba(76, 175, 80, 0.05)' }}>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom>
                          Current Admit Card
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <InfoRow
                          icon={<BadgeIcon sx={{ fontSize: 20, color: '#666' }} />}
                          label="Card Number"
                          value={profile.admitCardNumber}
                        />
                        <InfoRow
                          icon={<CalendarToday sx={{ fontSize: 20, color: '#666' }} />}
                          label="Generated On"
                          value={formatDate(profile.admitCardGeneratedDate)}
                        />
                        <InfoRow
                          icon={<EmojiEvents sx={{ fontSize: 20, color: '#666' }} />}
                          label="Event"
                          value={profile.selectedEvent?.eventName}
                        />
                        <InfoRow
                          icon={<CheckCircle sx={{ fontSize: 20, color: '#666' }} />}
                          label="Status"
                          value={<Chip label="Active" color="success" size="small" />}
                        />

                        <Box mt={3}>
                          <StyledButton
                            variant="contained"
                            fullWidth
                            startIcon={<Download />}
                            onClick={handleDownloadAdmitCard}
                            disabled={downloading}
                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                          >
                            {downloading ? 'Downloading...' : 'Download Admit Card'}
                          </StyledButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Admit Card History */}
                  {profile.admitCardHistory && profile.admitCardHistory.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Card sx={{ border: '1px solid #e0e0e0' }}>
                        <CardContent>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            Previous Admit Cards (History)
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          
                          {profile.admitCardHistory.map((card, index) => (
                            <Accordion key={index}>
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                  <Typography variant="body2" fontWeight="500">
                                    Card #{card.admitCardNumber}
                                  </Typography>
                                  <Chip
                                    label={card.isActive ? 'Active' : 'Replaced'}
                                    color={card.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{ mr: 1 }}
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <InfoRow
                                  icon={<EmojiEvents sx={{ fontSize: 18, color: '#666' }} />}
                                  label="Event"
                                  value={card.eventName}
                                />
                                <InfoRow
                                  icon={<CalendarToday sx={{ fontSize: 18, color: '#666' }} />}
                                  label="Generated"
                                  value={formatDate(card.generatedDate)}
                                />
                                {card.replacedBy && (
                                  <InfoRow
                                    icon={<TrendingUp sx={{ fontSize: 18, color: '#666' }} />}
                                    label="Replaced By"
                                    value={`Card #${card.replacedBy}`}
                                  />
                                )}
                                <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
                                  <Typography variant="caption">
                                    This card has been replaced by a newer version. For reference only.
                                  </Typography>
                                </Alert>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Alert severity="warning" icon={<Warning />}>
                  <Typography variant="body1" fontWeight="bold" mb={0.5}>
                    Admit Card Not Available
                  </Typography>
                  <Typography variant="body2">
                    {!isPaymentCompleted
                      ? 'Complete payment to generate your admit card'
                      : !isVerified
                      ? 'Your documents are being verified. Admit card will be generated after approval.'
                      : 'Your admit card is being generated. Please check back soon.'}
                  </Typography>
                </Alert>
              )}
            </StyledPaper>
          </Grid>

          {/* Documents Section */}
          <Grid item xs={12}>
            <StyledPaper>
              <SectionHeader>
                <Description sx={{ fontSize: 28, color: '#291e6c' }} />
                <Typography variant="h5" fontWeight="bold">
                  Uploaded Documents
                </Typography>
              </SectionHeader>

              <Grid container spacing={2}>
                {/* Identity Proof */}
                {profile.identityProof?.type && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Identity Proof
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {profile.identityProof.type}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {profile.identityProof.front?.path && (
                            <ImagePreview
                              src={profile.identityProof.front.path}
                              alt="Identity Proof Front"
                              onClick={() => handleImagePreview(profile.identityProof.front.path)}
                            />
                          )}
                          {profile.identityProof.back?.path && (
                            <ImagePreview
                              src={profile.identityProof.back.path}
                              alt="Identity Proof Back"
                              onClick={() => handleImagePreview(profile.identityProof.back.path)}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Proof of Age */}
                {profile.proofOfAge?.type && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Proof of Age
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {profile.proofOfAge.type}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {profile.proofOfAge.front?.path && (
                            <ImagePreview
                              src={profile.proofOfAge.front.path}
                              alt="Proof of Age Front"
                              onClick={() => handleImagePreview(profile.proofOfAge.front.path)}
                            />
                          )}
                          {profile.proofOfAge.back?.path && (
                            <ImagePreview
                              src={profile.proofOfAge.back.path}
                              alt="Proof of Age Back"
                              onClick={() => handleImagePreview(profile.proofOfAge.back.path)}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Address Proof */}
                {profile.addressProof?.type && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Address Proof
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          {profile.addressProof.type}
                        </Typography>
                        <Box display="flex" gap={2} flexWrap="wrap">
                          {profile.addressProof.front?.path && (
                            <ImagePreview
                              src={profile.addressProof.front.path}
                              alt="Address Proof Front"
                              onClick={() => handleImagePreview(profile.addressProof.front.path)}
                            />
                          )}
                          {profile.addressProof.back?.path && (
                            <ImagePreview
                              src={profile.addressProof.back.path}
                              alt="Address Proof Back"
                              onClick={() => handleImagePreview(profile.addressProof.back.path)}
                            />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {/* Medical Certificate */}
                {profile.medicalCertificate?.path && (
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Medical Certificate
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                          Required for Junior/Youth categories
                        </Typography>
                        <ImagePreview
                          src={profile.medicalCertificate.path}
                          alt="Medical Certificate"
                          onClick={() => handleImagePreview(profile.medicalCertificate.path)}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </StyledPaper>
          </Grid>
        </Grid>

        {/* Image Preview Dialog */}
        <Dialog open={openImageDialog} onClose={() => setOpenImageDialog(false)} maxWidth="md" fullWidth>
          <DialogContent sx={{ position: 'relative', p: 0 }}>
            <IconButton
              onClick={() => setOpenImageDialog(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </PageWrapper>
  );
};

export default Dashboard;